import { Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, Renderer2, SimpleChanges } from '@angular/core';
import * as pdfjs from 'pdfjs-dist/legacy/build/pdf.mjs';
import { GlobalWorkerOptions, PageViewport } from 'pdfjs-dist/legacy/build/pdf.mjs';
import { fromEvent, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { PDFDocumentProxy } from 'pdfjs-dist/types/web/pdf_viewer';

@Component({
  selector: 'app-pdf-viewer',
  standalone: true,
  templateUrl: './pdf-viewer.component.html',
  styleUrls: ['./pdf-viewer.component.scss'],
})
export class PdfViewerComponent implements OnInit, OnChanges, OnDestroy {

  @Input() src: string | ArrayBuffer | Uint8Array | Blob;
  @Input() displayMode: 'fitToPage' | 'actualSize' | 'fitWidth' = 'fitWidth';
  @Input() pagePadding: string = '10px';
  @Input() autoResize: boolean = true;

  @Output() afterLoadComplete = new EventEmitter<PDFDocumentProxy>();

  private pdf: PDFDocumentProxy;
  private resizeSubscription: Subscription;
  private resizeObserver: ResizeObserver;

  private loading: boolean = false;
  private pendingResize: boolean = false;

  constructor(private elementRef: ElementRef, private renderer: Renderer2) {
    GlobalWorkerOptions.workerSrc = '/assets/pdfjs/pdf.worker.min.mjs?v=4.8.69';
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      (changes['src'] && !changes['src'].firstChange) ||
      (changes['displayMode'] && !changes['displayMode'].firstChange) ||
      (changes['pagePadding'] && !changes['pagePadding'].firstChange)
    ) {
      this.reloadPdf();
    }

    if (changes['autoResize'] && !changes['autoResize'].firstChange) {
      if (this.autoResize) {
        this.setupResizeListener();
      } else {
        this.teardownResizeListener();
      }
    }
  }

  ngOnInit(): void {
    console.log('PDF viewer initialized');
    this.loadPdf().catch(error => console.error('Error loading PDF:', error));

    if (this.autoResize) {
      this.setupResizeListener();
    }
  }

  ngOnDestroy() {
    console.log('Destroying PDF viewer');
    this.cleanup().catch(error => console.error('Error during cleanup on destroy:', error));
    this.teardownResizeListener();
  }

  async loadPdf(): Promise<void> {
    if (!this.src) {
      console.error('No PDF source provided');
      return;
    }

    this.loading = true;

    try {
      const loadingTask = await this.getLoadingTask();
      this.pdf = await loadingTask.promise;
      await this.renderPdfPages();
    } catch (error) {
      console.error('Error loading PDF:', error);
    } finally {
      this.loading = false;
      // Handle any pending resize event after loading completes
      if (this.pendingResize) {
        this.pendingResize = false;
        this.handleResize();
      }
    }
  }

  private async getLoadingTask() {
    if (typeof this.src === 'string') {
      return pdfjs.getDocument(this.src);
    } else if (this.src instanceof Blob) {
      const arrayBuffer = await this.src.arrayBuffer();
      return pdfjs.getDocument({ data: arrayBuffer });
    } else if (this.src instanceof ArrayBuffer || this.src instanceof Uint8Array) {
      return pdfjs.getDocument({ data: this.src });
    } else {
      throw new Error('Unsupported PDF source type');
    }
  }

  private async renderPdfPages() {
    console.log('Rendering PDF pages');
    const numPages = this.pdf.numPages;

    // Get the container dimensions
    const containerElement = this.elementRef.nativeElement.querySelector('.pdf-viewer');
    const containerWidth = containerElement.clientWidth;
    const containerHeight = containerElement.clientHeight;

    const paddingValue = this.parsePadding(this.pagePadding);

    for (let pageNum = 1; pageNum <= numPages; pageNum++) {
      const page = await this.pdf.getPage(pageNum);
      const viewport = page.getViewport({ scale: 1.0 });

      const scale = this.calculateScale(viewport, containerWidth, containerHeight, paddingValue);
      const scaledViewport = page.getViewport({ scale });

      const canvas = this.renderer.createElement('canvas') as HTMLCanvasElement;
      this.renderer.addClass(canvas, 'pdf-page-canvas');

      // Set padding via inline style
      this.renderer.setStyle(canvas, 'padding', this.pagePadding);

      // Adjust canvas dimensions to account for padding
      const adjustedWidth = scaledViewport.width; // + paddingValue.left + paddingValue.right;
      const adjustedHeight = scaledViewport.height; // + paddingValue.top + paddingValue.bottom;

      canvas.width = adjustedWidth;
      canvas.height = adjustedHeight;

      const context = canvas.getContext('2d');

      const renderContext = {
        canvasContext: context,
        viewport: scaledViewport,
      };

      await page.render(renderContext).promise;

      this.renderer.appendChild(containerElement, canvas);
    }
  }

  private calculateScale(viewport: PageViewport, containerWidth: number, containerHeight: number, paddingValue: {
    top: number;
    right: number;
    bottom: number;
    left: number
  }): number {
    let scale = 1.0;
    const totalHorizontalPadding = paddingValue.left + paddingValue.right;
    const totalVerticalPadding = paddingValue.top + paddingValue.bottom;

    if (this.displayMode === 'actualSize') {
      scale = 1.0;
    } else if (this.displayMode === 'fitWidth') {
      scale = (containerWidth - totalHorizontalPadding - 8 /*scroll bar*/) / viewport.width;
    } else if (this.displayMode === 'fitToPage') {
      scale = Math.min(
        (containerWidth - totalHorizontalPadding) / viewport.width,
        (containerHeight - totalVerticalPadding) / viewport.height,
      );
    }

    if (!scale || !isFinite(scale) || scale <= 0) {
      console.warn(
        'Invalid scale calculated. Defaulting to scale = 1.0. Check container dimensions.',
      );
      scale = 1.0;
    }

    console.log(`Calculated scale: ${scale} for display mode: ${this.displayMode}`);

    return scale;
  }

  private async reRenderPdf() {
    // Prevent multiple reloads if already loading
    if (this.loading) {
      return;
    }

    try {
      // Remove all canvases
      const container = this.elementRef.nativeElement.querySelector('.pdf-viewer');
      while (container.firstChild) {
        container.removeChild(container.firstChild);
      }

      await this.renderPdfPages();
    } catch (error) {
      console.error('Error reloading PDF:', error);
    }
  }

  private async reloadPdf() {
    // Prevent multiple reloads if already loading
    if (this.loading) {
      return;
    }

    try {
      await this.cleanup();
      await this.loadPdf();
    } catch (error) {
      console.error('Error reloading PDF:', error);
    }
  }

  private async cleanup(): Promise<void> {
    // Remove all canvases
    const container = this.elementRef.nativeElement.querySelector('.pdf-viewer');
    while (container.firstChild) {
      container.removeChild(container.firstChild);
    }

    // Destroy the PDF document if it exists
    if (this.pdf) {
      try {
        await this.pdf.destroy();
      } catch (error) {
        console.error('Error destroying PDF:', error);
      } finally {
        this.pdf = null;
      }
    }
  }

  private parsePadding(padding: string): { top: number; right: number; bottom: number; left: number } {
    // Split the padding string into individual values
    const paddingValues = padding.split(' ').map((value) => parseFloat(value));

    let top, right, bottom, left;

    if (paddingValues.length === 1) {
      top = right = bottom = left = paddingValues[0];
    } else if (paddingValues.length === 2) {
      top = bottom = paddingValues[0];
      right = left = paddingValues[1];
    } else if (paddingValues.length === 3) {
      top = paddingValues[0];
      right = left = paddingValues[1];
      bottom = paddingValues[2];
    } else if (paddingValues.length === 4) {
      top = paddingValues[0];
      right = paddingValues[1];
      bottom = paddingValues[2];
      left = paddingValues[3];
    } else {
      top = right = bottom = left = 0;
    }

    return { top, right, bottom, left };
  }

  private setupResizeListener() {
    const containerElement = this.elementRef.nativeElement.querySelector('.pdf-viewer');

    /*if ('ResizeObserver' in window) {
      // Use ResizeObserver if available
      const resizeObservable$ = new Observable<ResizeObserverEntry[]>((observer) => {
        const resizeObserver = new ResizeObserver((entries) => observer.next(entries));
        resizeObserver.observe(containerElement);
        return () => resizeObserver.disconnect();
      });

      this.resizeSubscription = resizeObservable$
        .pipe(debounceTime(200))
        .subscribe(() => {
          this.handleResize();
        });
    } else {*/
      // Fallback to window resize events
      this.resizeSubscription = fromEvent(window, 'resize')
        .pipe(debounceTime(500))
        .subscribe(() => {
          this.handleResize();
        });
    //}
  }

  private teardownResizeListener() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    if (this.resizeSubscription) {
      this.resizeSubscription.unsubscribe();
      this.resizeSubscription = null;
    }
  }

  private handleResize() {
    if (this.loading) {
      // If loading, set pendingResize to true
      this.pendingResize = true;
    } else {
      //requestAnimationFrame(() => {
        this.reRenderPdf();
      //});
    }
  }
}
